"use client";

import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface RecruitersCaseStudyProps {
  story: {
    slug: string;
    name: string;
    role: string;
    location: string;
    image: string;
    image_alt?: string;
  };
  /** Override the story portrait for this page only (the shared story image
   * is a landscape crop used elsewhere). */
  imageSrc?: string;
}

export function RecruitersCaseStudy({
  story,
  imageSrc,
}: RecruitersCaseStudyProps) {
  return (
    <section className="py-16 md:py-24">
      <Container>
        <div className="mb-12 flex flex-col items-center gap-4">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              From recruiting partner to operator
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[820px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            Real experts. Real payouts. Real work.
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, transform: "translateY(24px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="border-marketing-border bg-marketing-cream-light mx-auto max-w-6xl overflow-hidden rounded-[24px] border"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            {/* Portrait */}
            <div className="bg-marketing-card-blue/30 relative aspect-[4/5] w-full lg:aspect-auto lg:min-h-[520px]">
              <Image
                src={imageSrc ?? story.image}
                alt={story.image_alt || `Portrait of ${story.name}`}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 40vw"
              />
            </div>

            {/* Content */}
            <div className="flex flex-col justify-between px-7 py-9 md:px-10 md:py-12">
              <div>
                <p className="text-marketing-blue font-mono text-[12px] tracking-[-0.2px] uppercase">
                  {story.role} · {story.location}
                </p>
                <h3 className="text-marketing-text dark:text-marketing-cream mt-4 text-2xl leading-[1.2] font-semibold tracking-tight md:text-3xl lg:text-[34px]">
                  &ldquo;I started by referring people I trusted into the panel:
                  friends, friends of friends, the niche operators no recruiter
                  could find.&rdquo;
                </h3>
                <p className="text-marketing-text-muted mt-6 text-base leading-relaxed md:text-lg">
                  Claire didn&apos;t join Terac as an employee. She joined as a
                  recruiting partner and started quietly placing people she
                  trusted: the niche operators no LinkedIn search could surface.
                  After a few months of doing that, Terac offered her a
                  full-time role running Talent Ops.
                </p>
              </div>

              {/* Hero stat */}
              <div className="border-marketing-border mt-9 border-t pt-7">
                <div className="text-marketing-text dark:text-marketing-cream font-serif text-5xl leading-none font-semibold tracking-tight md:text-6xl">
                  240+
                </div>
                <p className="text-marketing-text-muted mt-2 text-[13px] leading-snug md:text-sm">
                  Hires sourced through the partner system Claire helped
                  formalize.
                </p>
              </div>

              {/* CTA row */}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  href={`/stories/${story.slug}`}
                  className="bg-marketing-blue inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90"
                >
                  Read Claire&apos;s story
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  href="/stories"
                  className="text-marketing-text dark:text-marketing-cream hover:text-marketing-blue inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 transition-colors hover:underline"
                >
                  More stories
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
